import { forwardRef, useImperativeHandle, useRef, useState, useMemo } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import H4 from '@sdk/components/ui/typography/H4';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import Button from '@sdk/components/ui/buttons/Button';
import gettext from '@sdk/lang/gettext';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Modal from '@sdk/components/ui/modal/Modal';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import PickerField from '@sdk/components/forms/PickerField';
import { SUBSCRIPTION_BILLING_CYCLES, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Spinner from '@sdk/components/ui/Spinner';
import moneyDisplay from '@sdk/common/currency/moneyDisplay';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import DashboardBlueSvg from '@web/assets/svg/Icons/32/dashboard-blue-business.svg';
import CurrentBillingPricing from '@web/account/billing/CurrentBillingPricing/CurrentBillingPricing';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import CreditCardModal from '@web/account/billing/CreditCardModal/CreditCardModal';
import type { SubscriptionUtilsType } from '@web/account/types';
import { WithStripeManualPaymentMethod } from '@web/account/components/WithStripeManualPaymentMethod';
import { getIsYearlyPlan } from '@web/account/utils';

import { useUpgradeSubscriptionModal } from './upgradeSubscriptionModal.hooks';
import {
  BillingDetailsModalContainer,
  ModalContent,
  FooterRow,
  ButtonContainer,
  WhiteModalHeader,
} from './upgradeSubscriptionModal.styles';

type Props = {
  orgSettings: OrgSettings;
  currency: CurrencyType;
  subscriptionUtils: SubscriptionUtilsType;
  onSuccessSubmitCallback?: () => void;
};

type ForwardedAttrs = {
  open: () => void;
};

const UpgradeSubscriptionModal = forwardRef<ForwardedAttrs, Props>(
  ({ currency, orgSettings, subscriptionUtils, onSuccessSubmitCallback }, ref) => {
    const { billingInfo } = useBillingInfo();
    const currentOrFormerLevel =
      billingInfo.subscriptionLevel || billingInfo.formerSubscriptionLevel;
    const currentOrFormerIsYearlyPlan = billingInfo.subscriptionLevel
      ? billingInfo.isYearlyPlan
      : billingInfo.formerIsYearlyPlan;

    const currentBillingCycle = currentOrFormerIsYearlyPlan
      ? SUBSCRIPTION_BILLING_CYCLES.ANNUAL
      : SUBSCRIPTION_BILLING_CYCLES.MONTHLY;

    const { isVisible, open, close } = useModalState();
    const [level, setLevel] = useState(currentOrFormerLevel);
    const [upgradeCost, setUpgradeCost] = useState(null);
    const [subscriptionBillingCycle, setSubscriptionBillingCycle] = useState(currentBillingCycle);
    const config = useRef({ noDismiss: true }).current;
    const isYearlyPlan = getIsYearlyPlan(subscriptionBillingCycle);

    const {
      areYearlySubscriptionsEnabled,
      subscriptionCycles,
      subscriptionLevelsWithCurrrent,
      showPayingToday,
      isAnythingChanged,
      onSubmit,
      showPlanPicker,
      showCyclePicker,
      getUpgradeCost,
      overriddenNextChargeDate,
      creditCardModalState,
    } = useUpgradeSubscriptionModal({
      subscriptionUtils,
      level,
      currency,
      subscriptionBillingCycle,
      orgSettings,
      currentBillingCycle,
      setUpgradeCost,
      onSuccessSubmitCallback,
    });

    useImperativeHandle(ref, () => ({
      open(initialLevel = null) {
        setLevel(initialLevel ?? currentOrFormerLevel);

        if (isYearlyPlan && billingInfo.subscriptionLevel) {
          getUpgradeCost(initialLevel ?? currentOrFormerLevel);
        }
        open();
      },
    }));

    const totalPrice = useMemo(
      () => subscriptionUtils.getTotal(billingInfo, level, true, isYearlyPlan),
      [subscriptionUtils, billingInfo, level, subscriptionBillingCycle]
    );

    return (
      <>
        <Modal fixed config={config} isVisible={isVisible} onDismiss={close} closePositionTop={24}>
          <BillingDetailsModalContainer>
            <WhiteModalHeader pTop={20} pBottom={20}>
              <DashboardBlueSvg />
              <H4 themeColor={ThemeColorType.primary}>
                {level > currentOrFormerLevel
                  ? gettext('Upgrade to Business')
                  : gettext('Change billing cycle')}
              </H4>
            </WhiteModalHeader>
            <ModalContent>
              {showPlanPicker && (
                <>
                  <Row>
                    <Text14 semiBold themeColor={ThemeColorType.gray}>
                      {gettext('Plan')}
                    </Text14>
                  </Row>
                  <Row mTop={10} mBottom={20}>
                    <PickerField
                      options={subscriptionLevelsWithCurrrent}
                      value={level}
                      onChange={(_level: number) => {
                        setLevel(_level);

                        if (isYearlyPlan && billingInfo.subscriptionLevel) {
                          getUpgradeCost(_level);
                        }
                      }}
                    />
                  </Row>
                </>
              )}
              {areYearlySubscriptionsEnabled && showCyclePicker && (
                <>
                  <Row>
                    <Text14 semiBold themeColor={ThemeColorType.gray}>
                      {gettext('Billing cycle')}
                    </Text14>
                  </Row>
                  <Row mTop={10} mBottom={20}>
                    <PickerField
                      options={subscriptionCycles}
                      value={subscriptionBillingCycle}
                      onChange={(_cycle: SUBSCRIPTION_BILLING_CYCLES) => {
                        setSubscriptionBillingCycle(_cycle);

                        if (
                          _cycle === SUBSCRIPTION_BILLING_CYCLES.ANNUAL &&
                          billingInfo.subscriptionLevel
                        ) {
                          getUpgradeCost(level);
                        }
                      }}
                    />
                  </Row>
                </>
              )}
              {!showPlanPicker && !showCyclePicker && level === SUBSCRIPTION_LEVELS.BUSINESS && (
                <Row mBottom={10}>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext(
                      'We are happy you decided to switch over to Business - out best plan that unlocks all features!'
                    )}
                  </Text14>
                </Row>
              )}
              <Row mBottom={10}>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {isAnythingChanged
                    ? gettext(
                        'After the change your account will be billed %(cycle)s as follows:',
                        {
                          cycle: isYearlyPlan ? 'annually' : 'monthly',
                        }
                      )
                    : gettext('Your next invoice:')}
                </Text14>
              </Row>
              <HorizontalSeparator />
              <CurrentBillingPricing
                level={level}
                isYearlyPlan={isYearlyPlan}
                subscriptionUtils={subscriptionUtils}
                orgSettings={orgSettings}
                currency={currency}
                ignoreNextCharge={true}
                hideNextChargeDate={
                  Boolean(overriddenNextChargeDate) || !billingInfo.nextChargeDate
                }
              />
              <Row mTop={20}>
                <HorizontalSeparator />
              </Row>
              {showPayingToday && (
                <Row mTop={20}>
                  <Text14 themeColor={ThemeColorType.gray}>{gettext('Paying today')}</Text14>
                  <Row mLeft={'auto'}>
                    {upgradeCost !== null ? (
                      <Text14 themeColor={ThemeColorType.gray}>
                        {moneyDisplay({ value: upgradeCost, currency })}
                      </Text14>
                    ) : (
                      <Spinner />
                    )}
                  </Row>
                </Row>
              )}
              {Boolean(overriddenNextChargeDate) && (
                <Row alignItems="center" mTop={20}>
                  <Text14 semiBold themeColor={ThemeColorType.gray}>
                    {gettext('Next payment')}
                  </Text14>
                  <Row mLeft={'auto'}>
                    <Text14 semiBold themeColor={ThemeColorType.gray}>
                      {dateToHumanFormat(overriddenNextChargeDate)}
                    </Text14>
                  </Row>
                </Row>
              )}
            </ModalContent>

            <FooterRow>
              <ButtonContainer pRight={10}>
                <Button
                  buttonType={ButtonType.default}
                  onClick={() => {
                    close();
                  }}>
                  {gettext('Cancel')}
                </Button>
              </ButtonContainer>

              <ButtonContainer>
                <ProgressButton
                  buttonType={ButtonType.primary}
                  disabled={!isAnythingChanged}
                  onClick={async () => {
                    await onSubmit();
                    close();
                  }}
                  height={36}>
                  {level > currentOrFormerLevel ? gettext('Upgrade') : gettext('Update')}
                </ProgressButton>
              </ButtonContainer>
            </FooterRow>
          </BillingDetailsModalContainer>
        </Modal>
        <ToastComponent />
        <WithStripeManualPaymentMethod totalPrice={totalPrice}>
          <CreditCardModal {...creditCardModalState} />
        </WithStripeManualPaymentMethod>
      </>
    );
  }
);

UpgradeSubscriptionModal.displayName = 'UpgradeSubscriptionModal';

export default UpgradeSubscriptionModal;
